import {combine, guard, restore, sample} from 'effector';
import i18next from 'i18next';

import {toast} from 'react-toastify';

import {setXmlFile} from 'components/pages/DocumentsCreate/InvoiceNew/helpers/buildXml';
import {
    isStandartCategory,
    isZeroCategory,
    VAT_CATEGORY,
} from 'components/pages/DocumentsCreate/InvoiceNew/helpers/consts';
import {RestError, showRestError} from 'core/errors';
import {ROUTE} from 'core/routes';
import {setLastCreatedInvocieId, waitDocumentInGridAndRedirectFx} from 'models/documents/create';
import {PackageTypes, TRANSPORT_PACKAGE_TYPES} from 'types/gtins';
import {InvocieLineHolder, InvoiceLine, TaxesAndTotalAllowanceCharge} from 'types/invoices';

import {
    $allowedActionsForInvoice,
    $doesInvoiceExist,
    $invoiceIsRemoved,
    $invoiceState,
    CreateInvoiceGate,
    deleteInvoiceFx,
    setNewInvoiceIsDone,
    updateInvoiceBodyFx,
} from '../common';

import {setInvoiceCreatingPending} from '../details';

import {calcUpdatingTaxesAndTotalAllowanceCharge} from './helpers/calcChargeData';

import {errorHandlerNoFirs} from './helpers/errorHandlerNoFirs';

import {
    $forceAllowToSend,
    $infoValues,
    $initalLineValuesHolder,
    $invoiceCreationPage,
    $invoiceLineChargeData,
    $invoiceLinesHolder,
    $invoiceLineValues,
    $pageValues,
    $showGroupLinesByGtinModal,
    $taxesAndTotal,
    addInvoiceLineValues,
    arrStores,
    copyInvoiceLineData,
    fetchInvocieXmlFx,
    fetchInvoiceIrnFx,
    goNextPage,
    goPrevPage,
    guarderFetchInvoice,
    invoiceCreationPage,
    removeInvoiceLinesHolder,
    removeInvoiceLineValues,
    removeTaxesAndTotalAllowanceCharge,
    setForceAllowToSend,
    setInitalLineValuesHolder,
    setInvoiceLineChargeData,
    setInvoiceLinesHolder,
    setTaxesAndTotalFromXml,
    updateAllDataFromInvoiceLinesHolder,
    updateCustomerCompanyNameOrTIN,
    updateInfoValues,
    updateInfoValuesCityAndZip,
    updateInfoValuesPaymentMean,
    updateInvoiceLinesHolder,
    updateIRN,
    updatePageValues,
    updateTaxesAndTotal,
    updateTaxesAndTotalAllowanceCharge,
    updateTaxesAndTotalRoundAndPrepayd,
} from './index';

function getPath(depthId: string) {
    if (!depthId) return [];
    return depthId
        .toString()
        .split('.')
        .map(Number)
        .filter((n) => !Number.isNaN(n));
}

export function getRowAtPath(
    store: InvocieLineHolder[],
    depthId: string,
): InvocieLineHolder | undefined {
    const path = getPath(depthId);
    if (path.length === 0) return undefined;
    let current: InvocieLineHolder | undefined = store[path[0]];
    for (let i = 1; i < path.length; i++) {
        current = current?.subRows?.[path[i]];
    }
    return current;
}

fetchInvocieXmlFx.fail.watch(({error}) => showRestError(error as RestError));

fetchInvocieXmlFx.done.watch((data) => {
    const {reportId} = data.result.data.data;
    setLastCreatedInvocieId(reportId);

    setInvoiceCreatingPending(true);
    setNewInvoiceIsDone(true);
    waitDocumentInGridAndRedirectFx({
        reportId,
        redirectTo: ROUTE.DOCUMENTS_INVOICES_OUTGOING,
        updOnly: true,
    });
});

fetchInvoiceIrnFx.fail.watch(({error}) => {
    errorHandlerNoFirs(error);
    return showRestError(error as RestError);
});

$invoiceIsRemoved.on(deleteInvoiceFx.doneData, () => true);

$invoiceCreationPage.on(invoiceCreationPage, (_, data) => data);
$invoiceCreationPage.on(goNextPage, (store) => store + 1);
$invoiceCreationPage.on(goPrevPage, (store) => store - 1);

$pageValues.on(updatePageValues, (_, data) => data);

$infoValues.on(updateInfoValues, (store, data) => {
    const {
        customerZip: oldCustomerZip,
        customerCity: oldCustomerCity,
        customerCountry: oldCustomerCountry,
        customerStreet: oldCustomerStreet,
    } = store;
    const {
        customerZip: newCustomerZip,
        customerCity: newCustomerCity,
        customerCountry: newCustomerCountry,
        customerStreet: newCustomerStreet,
    } = data;

    return {
        ...data,
        customerZip: newCustomerZip ?? oldCustomerZip,
        customerCity: newCustomerCity ?? oldCustomerCity,
        customerCountry: newCustomerCountry ?? oldCustomerCountry,
        customerStreet: newCustomerStreet ?? oldCustomerStreet,
    };
});
$infoValues.on(updateInfoValuesCityAndZip, (store, data) => ({
    ...store,
    ...data,
}));
$infoValues.on(updateIRN, (store, data) => ({...store, irn: data}));
$infoValues.on(updateCustomerCompanyNameOrTIN, (store, data) => ({
    ...store,
    customerCompanyNameOrTIN: data,
}));

export const $infoValuesPaymentMean = restore(updateInfoValuesPaymentMean, false).reset(
    CreateInvoiceGate.close,
);

const REQUIRED_FIELDS: (keyof InvocieLineHolder)[] = [
    'qty',
    'price',
    'lines',
    'vatCategory',
    'vat',
    'lineAmountWVat',
];

const isFieldFilled = (
    line: InvocieLineHolder | undefined,
    field: keyof InvocieLineHolder,
): boolean => {
    if (!line) {
        return false;
    }

    if (!(field in line) || line[field] === undefined || line[field] === null) {
        return false;
    }

    const fieldValue = line[field];

    if (field === 'vatCategory' || field === 'serviceCategory') {
        const objValue = fieldValue as {value?: string} | undefined;
        return Boolean(objValue?.value && objValue.value !== '');
    }

    const stringValue = String(fieldValue);
    return stringValue !== '';
};

const areRequiredFieldsFilled = (line: InvocieLineHolder | undefined): boolean => {
    if (!line) {
        return false;
    }

    return REQUIRED_FIELDS.every((field) => isFieldFilled(line, field));
};

const hasChargesData = (line: InvocieLineHolder | undefined): boolean => {
    if (!line?.charges) {
        return false;
    }
    const {charges} = line;
    return Boolean(
        charges.amountXaf ||
            charges.reason ||
            charges.ratePercent !== undefined ||
            charges.discountRateAndBaseAmount !== undefined,
    );
};

const shouldDisablePasteForMarkedItem = (line: InvocieLineHolder | undefined): boolean => {
    if (!line) {
        return false;
    }

    const hasPrice = isFieldFilled(line, 'price');
    const hasVatCategory = isFieldFilled(line, 'vatCategory');
    const hasCharges = hasChargesData(line);

    return hasPrice || hasVatCategory || hasCharges;
};

export const calculateCopyActions = (line: InvocieLineHolder | undefined) => {
    const isRequiredFieldsFilled = areRequiredFieldsFilled(line);
    const isMarkedItem = line?.isMarkedItem ?? false;

    const allowToPaste = isMarkedItem ? !shouldDisablePasteForMarkedItem(line) : false;
    const allowToDelete = line?.copyActions?.allowToDelete ?? false;

    const allowToCopy =
        isMarkedItem &&
        isRequiredFieldsFilled &&
        !TRANSPORT_PACKAGE_TYPES.includes(line?.packagingType ?? ('' as PackageTypes));

    return {
        allowToCopy,
        allowToDuplicateDown: !isMarkedItem && isRequiredFieldsFilled,
        allowToPaste,
        allowToDelete,
    };
};

const updateLineWithCopyActions = (line: InvocieLineHolder): InvocieLineHolder => ({
    ...line,
    copyActions: calculateCopyActions(line),
});

$invoiceLinesHolder.on(updateAllDataFromInvoiceLinesHolder, (store, data) => data);

$invoiceLinesHolder.on(setInvoiceLinesHolder, (store, data) => {
    const dataWithCalculatedActions = data.map((line) => updateLineWithCopyActions(line));
    return [...dataWithCalculatedActions, ...store];
});
$invoiceLinesHolder.on(removeInvoiceLinesHolder, (store, id) => {
    const path = getPath(id);
    if (path.length === 0) return store;
    const [headIndex, ...rest] = path;
    return store.filter((_, indx) => headIndex !== indx);
});

function updateRowAtPath(
    store: InvocieLineHolder[],
    depthId: string,
    updater: (row: InvocieLineHolder) => InvocieLineHolder,
): InvocieLineHolder[] {
    const path = getPath(depthId);
    if (path.length === 0) return store;
    const [head, ...rest] = path;
    if (rest.length === 0) {
        const row = store[head];
        if (row == null) return store;
        return store.map((line, i) => (i === head ? updater(line) : line));
    }
    const row = store[head];
    if (row == null) return store;
    const updatedSubRows = updateRowAtPath(row.subRows ?? [], rest.join('.'), updater);
    return store.map((line, i) => (i === head ? {...line, subRows: updatedSubRows} : line));
}

$invoiceLinesHolder.on(updateInvoiceLinesHolder, (store, data) => {
    const {data: innerData, depth} = data;
    const storeHolder = [...store];
    const {columnId, value} = innerData as {columnId: string; value: string | null};

    const targetRow = getRowAtPath(storeHolder, depth.id);

    if (targetRow) {
        if (value === null) delete targetRow.charges;

        if (Array.isArray(innerData)) {
            const lineValuess = Object.values(innerData);
            return updateRowAtPath(storeHolder, depth.id, (row) => {
                const toUpd = lineValuess.reduce((acc, key) => {
                    const {columnId, value} = key;
                    Object.assign(acc, {[columnId]: value});

                    return acc;
                }, {} as {columnId: string; value: string | string[] | null});

                return updateLineWithCopyActions({...row, ...toUpd});
            });
        }

        return updateRowAtPath(storeHolder, depth.id, (row) =>
            updateLineWithCopyActions({...row, [columnId]: value}),
        );
    }
    return store;
});

const calculateLines = (line: InvocieLineHolder): string => {
    const {qty, price, charges} = line;

    if (!qty || !price || isNaN(Number(qty)) || isNaN(Number(price))) {
        return line.lines || '';
    }

    const baseValue = Number(price) * Number(qty);

    if (charges?.amountXaf) {
        const multiplier = charges.isCharge === 'true' ? 1 : -1;
        const xaf = Number(charges.amountXaf) * multiplier;
        return (baseValue + xaf).toFixed(2);
    }

    return baseValue.toFixed(2);
};

const calculateLineAmountWVat = (line: InvocieLineHolder): string => {
    const {lines, vat, vatCategory} = line;

    if (!vatCategory?.value || vat === '0' || !vat) {
        const linesValue = Number(lines);
        return isNaN(linesValue) ? line.lineAmountWVat || '' : linesValue.toFixed(2);
    }

    if (!lines || isNaN(Number(lines)) || isNaN(Number(vat))) {
        return line.lineAmountWVat || '';
    }

    const linesValue = Number(lines);
    const vatValue = Number(vat);
    const result = linesValue + (linesValue * vatValue) / 100;

    return result.toFixed(2);
};

function duplicateLineData(
    line: InvocieLineHolder,
    deepSiblingIndex: number | undefined,
    dataToCopy: Partial<InvocieLineHolder>,
    gtinToCopyFrom: string,
) {
    if (!line.packagingType || TRANSPORT_PACKAGE_TYPES.includes(line.packagingType)) {
        return line;
    }

    if (deepSiblingIndex === undefined) {
        return line;
    }

    const isMarkedItem = line.isMarkedItem === true;
    const allowToPaste = line.gtin === gtinToCopyFrom;

    if (isMarkedItem && allowToPaste) {
        const updatedLine: InvocieLineHolder = {
            ...line,
            ...dataToCopy,
        };
        updatedLine.lines = calculateLines(updatedLine);
        updatedLine.lineAmountWVat = calculateLineAmountWVat(updatedLine);
        return updateLineWithCopyActions(updatedLine);
    }

    return line;
}

function duplicateLineOnFirstLevel(
    store: InvocieLineHolder[],
    deepSiblingIndex: number | undefined,
    dataToCopy: Partial<InvocieLineHolder>,
    gtinToCopyFrom: string,
) {
    const updatedStore = [...store];
    const returnStore = updatedStore.map((line) =>
        duplicateLineData(line, deepSiblingIndex, dataToCopy, gtinToCopyFrom),
    );

    return returnStore;
}

function duplicateLineOnOtherLevels(
    store: InvocieLineHolder[],
    sourceIndex: number | undefined,
    parentIndex: string,
    dataToCopy: Partial<InvocieLineHolder>,
    gtinToCopyFrom: string,
) {
    const updatedStore = [...store];
    const parentRow = getRowAtPath(updatedStore, parentIndex);
    if (parentRow) {
        return updateRowAtPath(updatedStore, parentIndex, (row) => {
            const newSubRows = row.subRows?.map((subRow) =>
                duplicateLineData(subRow, sourceIndex, dataToCopy, gtinToCopyFrom),
            );

            return {
                ...row,
                subRows: newSubRows,
            };
        });
    }

    return store;
}

$invoiceLinesHolder.on(copyInvoiceLineData, (store, data) => {
    const {index: sourceIndex, initialLineData} = data;
    const sourceLine =
        sourceIndex === '-1' && initialLineData
            ? initialLineData
            : getRowAtPath(store, sourceIndex);
    if (!sourceLine) {
        return store;
    }

    const dataToCopy = {
        price: sourceLine.price,
        charges:
            sourceIndex === '-1' && initialLineData
                ? $invoiceLineChargeData.getState() ?? undefined
                : sourceLine.charges,
        vatCategory: sourceLine.vatCategory,
        vat: sourceLine.vat,
    };

    const depthLevel = sourceIndex.split('.').length;
    const parentIndex = sourceIndex
        .split('.')
        .slice(0, -1)
        .join('.');
    const deepSiblingIndex = sourceIndex
        .split('.')
        .map(Number)
        .pop();

    if (depthLevel === 1) {
        return duplicateLineOnFirstLevel(
            store,
            deepSiblingIndex,
            dataToCopy,
            sourceLine.gtin ?? '',
        );
    }

    return duplicateLineOnOtherLevels(
        store,
        deepSiblingIndex,
        parentIndex,
        dataToCopy,
        sourceLine.gtin ?? '',
    );
});

// $invoiceLineValues.on(addInvoiceLineValues, (store, data) => {
//     const invoiceLineChargeData = $invoiceLineChargeData.getState();
//     const mappedData = data.map((i, index) => {
//         const invoiceLineChargeDataItem = invoiceLineChargeData[index];
//         if (!invoiceLineChargeDataItem) {
//             return i;
//         }

//         return {
//             ...i,
//             charges: invoiceLineChargeDataItem,
//         };
//     });

//     return [...mappedData, ...store];
// });
$invoiceLineValues.on(addInvoiceLineValues, (_, data) => data);

$invoiceLineValues.on(removeInvoiceLineValues, (store, index) =>
    store.filter((j, indx) => index !== indx),
);

$initalLineValuesHolder.on(setInitalLineValuesHolder, (store, data) => ({
    ...store,
    ...data,
}));

function calculateTaxValues(val: InvoiceLine[]) {
    return val.reduce(
        (total, i) => {
            const totalMainKey = `${i.vatCategory?.value as keyof typeof total} :: ${Number(
                i.vat,
            )}`;

            if (totalMainKey in total) {
                const newTaxable1 = total[totalMainKey]['taxableAmount'] + Number(i.lines);
                const newTax =
                    total[totalMainKey]['taxAmount'] +
                    Number(i.lines) * (total[totalMainKey]['rate'] / 100);
                return {
                    ...total,
                    [totalMainKey]: {
                        rate: total[totalMainKey]['rate'],
                        taxableAmount: newTaxable1,
                        taxAmount: newTax,
                    },
                };
            }
            return {
                ...total,
                [totalMainKey]: {
                    taxableAmount: Number(i.lines),
                    rate: Number(i.vat),
                    taxAmount: Number(i.lines) * (Number(i.vat) / 100),
                },
            };
        },
        {} as Record<
            string,
            {
                taxableAmount: number;
                rate: number;
                taxAmount: number;
            }
        >,
    );
}

guard({
    clock: $invoiceLineValues,
    filter: () => $forceAllowToSend.getState(),
    target: guarderFetchInvoice,
});

const goToTaxesUpdate = guard({
    clock: $invoiceLineValues,
    filter: () => !$forceAllowToSend.getState(),
});

sample({
    clock: goToTaxesUpdate,
    fn: (val) => {
        const taxValues = calculateTaxValues(val);
        const taxCategoryDisplay = (key: string) =>
            VAT_CATEGORY.find((i) => i.value === key.split(' :: ')[0]);

        const taxes = $taxesAndTotal.getState();
        const valuesToTaxesBlock = Object.entries(taxValues).map(([key, value], index) => ({
            taxCategory: key,
            taxableAmount: value.taxableAmount.toFixed(2),
            rate: value.rate.toFixed(1),
            taxAmount: value.taxAmount.toFixed(2),

            taxCategoryDisplay: taxCategoryDisplay(key)?.label,
            taxableAmountDisplay: value.taxableAmount.toFixed(2),
            rateDisplay: value.rate.toFixed(1),
            taxAmountDisplay: value.taxAmount.toFixed(2),
            ...(!isStandartCategory(key) &&
                !isZeroCategory(key) && {
                    taxExemptionReason:
                        taxes.data.calculationsByTaxCategory[index]?.taxExemptionReason ?? '',
                }),
        }));
        const setTaxTotal = valuesToTaxesBlock.reduce((t, i) => t + Number(i.taxAmount), 0);
        const LEA = valuesToTaxesBlock.reduce((t, i) => t + Number(i.taxableAmount), 0);

        // const taxesAndTotalAllowanceCharge = $taxesAndTotalAllowanceCharge.getState()
        const calcTaxExclusiveAmount =
            LEA - Number(taxes.data.allowanceTotalAmount) + Number(taxes.data.chargeTotalAmount);
        const calcTaxInclusiveAmount = setTaxTotal + calcTaxExclusiveAmount;
        const calcPayableAmount =
            calcTaxInclusiveAmount -
            (taxes.data.prepaidAmount ? Number(taxes.data.prepaidAmount) : 0) +
            (taxes.data.payableRoundingAmount ? Number(taxes.data.payableRoundingAmount) : 0);
        return {
            data: {
                ...taxes.data,
                calculationsByTaxCategory: valuesToTaxesBlock,
                taxTotal: setTaxTotal.toFixed(2),
                lineExtensionAmount: LEA.toFixed(2),
                taxExclusiveAmount: calcTaxExclusiveAmount.toFixed(2),
                taxExclusiveAmountDisplay: calcTaxExclusiveAmount.toFixed(2),
                taxInclusiveAmount: calcTaxInclusiveAmount.toFixed(2),
                taxInclusiveAmountDisplay: calcTaxInclusiveAmount.toFixed(2),
                whtChargeAmount_1: (LEA * (3 / 100)).toFixed(2),
                payableAmount: calcPayableAmount.toFixed(2),
                payableAmountDisplay: calcPayableAmount.toFixed(2),
            },
            allowToSend: false,
        };
    },
    target: updateTaxesAndTotal,
});

$taxesAndTotal.on(updateTaxesAndTotal, (store, data) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {allowanceCharge, ...rest} = data.data;
    return {
        data: {
            ...store.data,
            ...rest,
        },
        allowToSend: data.allowToSend,
    };
});

$taxesAndTotal.on(setTaxesAndTotalFromXml, (_, data) => ({
    data: data.data,
    allowToSend: false,
}));

sample({
    clock: updateTaxesAndTotalAllowanceCharge,
    fn: calcUpdatingTaxesAndTotalAllowanceCharge,
    target: $taxesAndTotal,
});

sample({
    clock: removeTaxesAndTotalAllowanceCharge,
    fn: calcUpdatingTaxesAndTotalAllowanceCharge,
    target: $taxesAndTotal,
});

sample({
    clock: updateTaxesAndTotalRoundAndPrepayd,
    fn: (item) => {
        const {name, value} = item;
        const taxesAndTotal = $taxesAndTotal.getState();
        const amounts = {
            prepaidAmount: taxesAndTotal.data.prepaidAmount,
            payableRoundingAmount: taxesAndTotal.data.payableRoundingAmount,
        };
        const amountItem = {[name]: value};
        Object.assign(amounts, amountItem);

        const p = Number(amounts.payableRoundingAmount) || 0;
        const e = Number(amounts.prepaidAmount) || 0;

        const setPayableAmountDisplay =
            Number(taxesAndTotal.data.taxInclusiveAmountDisplay) - e + p;
        Object.assign(amounts, {
            payableAmountDisplay: setPayableAmountDisplay.toFixed(2),
        });

        return {
            data: {
                ...taxesAndTotal.data,
                ...amounts,
            },
            allowToSend: false,
        };
    },
    target: updateTaxesAndTotal,
});

export const $taxesAndTotalAllowanceCharge = $taxesAndTotal.map((item) => {
    if (!item.data.allowanceCharge) {
        return {
            charges: [],
            allowances: [],
        };
    }
    return item.data.allowanceCharge.reduce(
        (t, i, index) => {
            const {isCharge, isRatePercent, ...rest} = i;
            if (i.isCharge === 'allowance') {
                return {
                    ...t,
                    allowances: [...t.allowances, {...rest, mutualIndex: index}],
                };
            }

            return {
                ...t,
                charges: [...t.charges, {...rest, mutualIndex: index}],
            };
        },
        {
            charges: [],
            allowances: [],
        } as {
            charges: TaxesAndTotalAllowanceCharge[];
            allowances: TaxesAndTotalAllowanceCharge[];
        },
    );
});

$invoiceLineChargeData.on(setInvoiceLineChargeData, (_, data) => data);

$infoValues.reset(CreateInvoiceGate.close);
$invoiceCreationPage.reset(CreateInvoiceGate.close);
$pageValues.reset(CreateInvoiceGate.close);
$invoiceLineChargeData.reset(CreateInvoiceGate.close);
$invoiceLinesHolder.reset(CreateInvoiceGate.close);
$invoiceLineValues.reset(CreateInvoiceGate.close);
$taxesAndTotal.reset(CreateInvoiceGate.close);
$initalLineValuesHolder.reset(CreateInvoiceGate.close);

guard({
    clock: $taxesAndTotal,
    filter: (i) => $allowedActionsForInvoice.getState().edit && i.allowToSend,
    target: guarderFetchInvoice,
});

const saveInvoiceGuard = guard({
    clock: guarderFetchInvoice,
    filter: () => !$doesInvoiceExist.getState() && $allowedActionsForInvoice.getState().edit,
});

const updateInvoiceBodyGuard = guard({
    clock: guarderFetchInvoice,
    filter: () => $doesInvoiceExist.getState() && $allowedActionsForInvoice.getState().edit,
});

sample({
    clock: saveInvoiceGuard,
    source: arrStores,
    fn: (value) => setXmlFile(value),
    target: fetchInvocieXmlFx,
});

sample({
    clock: updateInvoiceBodyGuard,
    source: combine({
        reportId: $invoiceState,
        body: arrStores,
    }),
    fn: ({reportId, body}) => ({
        reportId: reportId ?? '',
        body: setXmlFile(body),
    }),
    target: updateInvoiceBodyFx,
});

$showGroupLinesByGtinModal.reset(CreateInvoiceGate.close);
$forceAllowToSend.reset(CreateInvoiceGate.close);
$showGroupLinesByGtinModal.watch((data) => {
    !data && setForceAllowToSend(false);
});
